import {
  Shield,
  Users,
  FileText,
  UserCheck,
  Settings,
  Contact,
  Building,
  Database,
  Lock,
  Globe,
  Search,
} from "lucide-react";

const formatCategoryName = (category: string) => {
  return category
    .replace(/-/g, " ") // Replace hyphens with spaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function to get category information
export const getCategoryInfo = (category: string) => {
  const formattedName = formatCategoryName(category);

  const categoryMap: Record<string, any> = {
    contacts: {
      label: "Διαχείριση Επαφών",
      title: "Επαφές & Συνδέσμοι",
      description: "Διαχείριση επαφών και συνδέσμων",
      icon: <Contact className="h-5 w-5" />,
    },
    "contact-group": {
      label: "Διαχείριση Ομάδων Επαφών",
      title: "Ομάδες Επαφών",
      description: "Διαχείριση ομάδων και συλλογών επαφών",
      icon: <Contact className="h-5 w-5" />,
    },
    files: {
      label: "Διαχείριση Αρχείων",
      title: "Έγγραφα & Αρχεία",
      description: "Διαχείριση εγγράφων και ψηφιακών αρχείων",
      icon: <FileText className="h-5 w-5" />,
    },
    "file-tags": {
      label: "Διαχείριση Ετικετών Αρχείων",
      title: "Ετικέτες Αρχείων",
      description: "Διαχείριση ετικετών και κατηγοριοποίησης αρχείων",
      icon: <FileText className="h-5 w-5" />,
    },
    iam: {
      label: "Διαχείριση Ταυτότητας",
      title: "Ταυτότητα & Πρόσβαση",
      description: "Διαχείριση ταυτότητας και εξουσιοδότησης",
      icon: <Lock className="h-5 w-5" />,
    },
    roles: {
      label: "Διαχείριση Ρόλων",
      title: "Ρόλοι & Δικαιώματα",
      description: "Διαχείριση ρόλων και δικαιωμάτων χρηστών",
      icon: <Users className="h-5 w-5" />,
    },
    departments: {
      label: "Διαχείριση Τμημάτων",
      title: "Τμήματα & Οργάνωση",
      description: "Διαχείριση τμημάτων και οργανωτικής δομής",
      icon: <Building className="h-5 w-5" />,
    },
    profile: {
      label: "Διαχείριση Προφίλ",
      title: "Προφίλ Χρηστών",
      description: "Διαχείριση προφίλ και στοιχείων χρηστών",
      icon: <UserCheck className="h-5 w-5" />,
    },
    permissions: {
      label: "Διαχείριση Δικαιωμάτων",
      title: "Δικαιώματα & Άδειες",
      description: "Διαχείριση δικαιωμάτων πρόσβασης",
      icon: <Shield className="h-5 w-5" />,
    },
    "permission-sets": {
      label: "Διαχείριση Συνόλων Δικαιωμάτων",
      title: "Σύνολα Δικαιωμάτων",
      description: "Διαχείριση προκαθορισμένων συνόλων δικαιωμάτων",
      icon: <Shield className="h-5 w-5" />,
    },
    organization: {
      label: "Διαχείριση Οργανισμού",
      title: "Οργανισμός & Ρυθμίσεις",
      description: "Διαχείριση οργανισμού και γενικών ρυθμίσεων",
      icon: <Globe className="h-5 w-5" />,
    },
    "organization-preferences": {
      label: "Προτιμήσεις Οργανισμού",
      title: "Προτιμήσεις & Ρυθμίσεις",
      description: "Διαχείριση προτιμήσεων και ρυθμίσεων οργανισμού",
      icon: <Settings className="h-5 w-5" />,
    },
    protocol: {
      label: "Διαχείριση Πρωτοκόλλων",
      title: "Πρωτόκολλα & Διαδικασίες",
      description: "Διαχείριση πρωτοκόλλων και διαδικασιών",
      icon: <Database className="h-5 w-5" />,
    },
    "protocol-category": {
      label: "Κατηγορίες Πρωτοκόλλων",
      title: "Κατηγορίες Πρωτοκόλλων",
      description: "Διαχείριση κατηγοριών πρωτοκόλλων",
      icon: <Database className="h-5 w-5" />,
    },
    "protocol-tag": {
      label: "Ετικέτες Πρωτοκόλλων",
      title: "Ετικέτες Πρωτοκόλλων",
      description: "Διαχείριση ετικετών πρωτοκόλλων",
      icon: <Database className="h-5 w-5" />,
    },
    "default-resource-attributes": {
      label: "Προεπιλεγμένα Χαρακτηριστικά Πόρων",
      title: "Προεπιλεγμένα Χαρακτηριστικά",
      description: "Διαχείριση προεπιλεγμένων χαρακτηριστικών πόρων",
      icon: <Settings className="h-5 w-5" />,
    },
    deployments: {
      label: "Διαχείριση Αναπτύξεων",
      title: "Αναπτύξεις & Εγκαταστάσεις",
      description: "Διαχείριση αναπτύξεων και εγκαταστάσεων συστήματος",
      icon: <Globe className="h-5 w-5" />,
    },
    "plan-ledger": {
      label: "Διαχείριση Λογιστικού Σχεδίου",
      title: "Λογιστικό Σχέδιο",
      description: "Διαχείριση λογιστικού σχεδίου και καταχωρήσεων",
      icon: <Database className="h-5 w-5" />,
    },
    record: {
      label: "Διαχείριση Εγγραφών",
      title: "Εγγραφές & Δεδομένα",
      description: "Διαχείριση εγγραφών και δεδομένων συστήματος",
      icon: <Database className="h-5 w-5" />,
    },
    "advanced-search": {
      label: "Προηγμένη Αναζήτηση",
      title: "Προηγμένη Αναζήτηση",
      description: "Εργαλεία προηγμένης αναζήτησης και φιλτραρίσματος",
      icon: <Search className="h-5 w-5" />,
    },
  };

  return (
    categoryMap[category] || {
      label: `Διαχείριση ${formattedName}`,
      title: formattedName,
      description: `Διαχείριση ${formattedName.toLowerCase()}`,
      icon: <Settings className="h-5 w-5" />,
    }
  );
};

export const generateRoleId = (name: string): string => {
  const safeName = name
    .toLowerCase()
    .replace(/[^a-zA-Z]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

  return `role.${safeName}`;
};
